import _filter from "lodash/filter";
import _find from "lodash/find";
import _findIndex from "lodash/findIndex";
import _get from "lodash/get";
import _isEmpty from "lodash/isEmpty";
import _map from "lodash/map";
import buildingService from "../../services/building.service";
import EmployeeService from "../../services/employee.service";
import propertyService from "../../services/property.service";
import { ERROR_MESSAGE, SEARCH_QUERY, SUCCESS_MESSAGE } from "../../utils/constants";
import { isTemporaryUnit, normalizeUnitId } from "../../utils/property.util";
import { throwError, to, toastMessage } from "../../utils/utils";
import {
  selectAreFeatureFlagsResolved,
  selectIsPropertySearchRedesignEnabled,
  selectPropertySearchRedesignClientState,
} from "../selectors/featureFlag.selector";
import { getAllFeatureFlags } from "./featureFlag.actions";
import ACTIONS from "./types.actions";

const LOADING = {
  BUILDING_LOADING: "loading",
  AUDIT_LOADING: "auditLoading",
  USERS_LOADING: "usersLoading",
};

// The selected-building store is single-valued, so only the latest request may update it.
let latestBuildingDetailsRequest;

const getPropertySearchRedesignWriteState = state => {
  if (!selectAreFeatureFlagsResolved(state)) {
    throw new Error("Feature flags are still loading. Please try again.");
  }
  return selectPropertySearchRedesignClientState(state);
};

const withPropertySearchRedesignState = (body, clientState) => {
  if (!body) return body;
  return clientState === undefined ? body : { ...body, propertySearchRedesignEnabled: clientState };
};

const refreshFeatureFlagsAfterWriteConflict = async (error, dispatch) => {
  if (_get(error, "response.status") !== 409) return;
  await to(dispatch(getAllFeatureFlags()));
};

export function buildingRequest(key = LOADING.BUILDING_LOADING) {
  return { type: ACTIONS.BUILDING_REQUEST, key };
}

export function buildingSuccess(response) {
  return { type: ACTIONS.BUILDING_SUCCESS, response };
}

export function buildingCodeSuccess(response) {
  return { type: ACTIONS.BUILDING_CODE_SUCCESS, response };
}

export function publishedUnitsSuccess(response) {
  return { type: ACTIONS.BUILDING_PUBLISHED_UNITS_SUCCESS, response };
}

export function buildingUOMSuccess(response) {
  return { type: ACTIONS.BUILDING_UOM_SUCCESS, response };
}

export function totalBuildingSizeSuccess(response) {
  return { type: ACTIONS.BUILDING_TOTAL_BUILDING_SIZE_SUCCESS, response };
}

export function buildingOccupiedSpaceSuccess(response) {
  return { type: ACTIONS.BUILDING_OCCUPIED_SPACE_SUCCESS, response };
}

export function buildingError(error) {
  return { type: ACTIONS.BUILDING_ERROR, error };
}

export function updateCombinedUnitNumber(maxCombinedUnitNumber) {
  return { type: ACTIONS.BUILDING_COMBINED_UNIT_NUMBER, maxCombinedUnitNumber };
}

export function getMarketUsersSuccess() {
  return { type: ACTIONS.GET_MARKET_USERS_SUCCESS };
}

export function unitDetailSuccess(response) {
  return { type: ACTIONS.BUILDING_UNIT_DETAIL_SUCCESS, response };
}

/**
 * To update building record which doesn't have
 * never published status
 */
export function updateBuilding(id, buildingInfo, successMsg) {
  return async (dispatch, getState) => {
    dispatch(buildingRequest(LOADING.BUILDING_LOADING));
    try {
      const clientState = getPropertySearchRedesignWriteState(getState());
      const [bynderErr, updatedBuildingInfo] = await to(
        buildingService.updatePropertyWithBynderDATUrls(id, buildingInfo),
      );
      if (bynderErr) throwError(bynderErr);

      const [err, response] = await to(
        buildingService.updateBuildingById(id, withPropertySearchRedesignState(updatedBuildingInfo, clientState)),
      );
      if (err) throwError(err);

      toastMessage("success", successMsg || SUCCESS_MESSAGE.EDITED);
      dispatch(buildingRequest(LOADING.BUILDING_LOADING));
    } catch (error) {
      await refreshFeatureFlagsAfterWriteConflict(error, dispatch);
      dispatch(buildingError(error));
      toastMessage("error", ERROR_MESSAGE.EDITED);
      throwError(error);
    }
  };
}

/**
 * To update unit record for which building doesn't have
 * never published status
 */
export function updateUnit(body, successMsg, unitIndexToSave) {
  return async (dispatch, getState) => {
    dispatch(buildingRequest(LOADING.BUILDING_LOADING));
    try {
      const clientState = getPropertySearchRedesignWriteState(getState());
      const [err, response] = await to(buildingService.updateUnit(withPropertySearchRedesignState(body, clientState)));
      const updatedUnitInfo = _get(response, "unitInfo", []);
      const unitDetail = { source: updatedUnitInfo, index: unitIndexToSave };
      if (err) throwError(err);
      dispatch(unitDetailSuccess(unitDetail));

      toastMessage("success", successMsg || SUCCESS_MESSAGE.EDITED);
    } catch (error) {
      await refreshFeatureFlagsAfterWriteConflict(error, dispatch);
      dispatch(buildingError(error));
      toastMessage("error", ERROR_MESSAGE.EDITED);
      throwError(error);
    }
  };
}

/**
 * To add building record which have
 * never published status
 */
export function addBuilding(buildingInfo) {
  return async (dispatch, getState) => {
    dispatch(buildingRequest(LOADING.BUILDING_LOADING));
    try {
      const clientState = getPropertySearchRedesignWriteState(getState());
      const [err, response] = await to(
        buildingService.addBuilding(withPropertySearchRedesignState(buildingInfo, clientState)),
      );
      if (err) throwError(err);

      toastMessage("success", SUCCESS_MESSAGE.ADDED);
      dispatch(buildingSuccess({}));
    } catch (error) {
      await refreshFeatureFlagsAfterWriteConflict(error, dispatch);
      dispatch(buildingError(error));
      toastMessage("error", ERROR_MESSAGE.ADDED);
      throwError(error);
    }
  };
}

/**
 * To get building record along with its characteristics
 * and details coming from snowflake
 */
export function getBuildingById(id) {
  return async (dispatch, getState) => {
    const requestToken = {};
    latestBuildingDetailsRequest = requestToken;
    const isLatestRequest = () => latestBuildingDetailsRequest === requestToken;

    dispatch(buildingRequest(LOADING.BUILDING_LOADING));
    try {
      let building, err, pCode;

      /**
       * Building details with characteristics and available units
       * from snowflake
       */
      const isBudSyncEnabled = selectIsPropertySearchRedesignEnabled(getState());
      const propertyCodeRequest = {
        ...SEARCH_QUERY,
        id,
        ...(isBudSyncEnabled && { includeDpm: false }),
      };
      [err, pCode] = await to(propertyService.getPropertyCodes(propertyCodeRequest));
      if (!isLatestRequest()) return undefined;
      if (err) throwError(err);

      /**
       * Building details added into DPM
       */
      [err, building] = await to(buildingService.getBuildingById(id));

      if (!isLatestRequest()) return undefined;
      if (err) throwError(err);

      if (_isEmpty(pCode) && _isEmpty(building)) {
        toastMessage("error", "No record found!");
        return dispatch(buildingSuccess({}));
      }
      const { unitInfo, ...rest } = pCode;
      const _uInfo = unitInfo || [];

      /**
       * Merge snowflake units and new units created in DPM
       */
      const pCodeUnits = new Set(_uInfo.map(d => normalizeUnitId(d.unitId)));
      const _unitInfo = [
        ..._uInfo,
        ...(building?.unitInfo || []).filter(d => isTemporaryUnit(d) || !pCodeUnits.has(normalizeUnitId(d?.unitId))),
      ];
      const _availableUnits = _map(_unitInfo, ({ unitId: _unitId, ...r }) => {
        const normalizedUnitId = normalizeUnitId(_unitId);
        const isAvailable =
          _findIndex(building?.unitInfo || [], unit => normalizeUnitId(unit?.unitId) === normalizedUnitId) === -1;
        return {
          ...r,
          unitId: _unitId,
          isAvailable,
          isNew: !pCodeUnits.has(normalizedUnitId),
        };
      });

      pCode = { ...rest, units: _availableUnits };

      // characteristics
      if (!building) {
        building = {
          status: pCode.status,
          market: { ...pCode?.market },
          subMarket: pCode?.subMarket,
          buildingInfo: {
            propertyCode: pCode?.id,
            marketingPropertyName: pCode?.propertyInfo?.marketingPropertyName || 0,
            truckCourtDepth: Number(pCode?.characteristics?.truckCourtDepth || 0).toFixed(4),
            railServed: pCode?.characteristics?.railServed === "1",
            totalBuildingSize: pCode?.propertyInfo?.totalBuildingSize,
            totalBuildingSizeUOM: pCode?.propertyInfo?.totalBuildingSizeUOM,
            address: {
              streetAddress1: pCode?.address?.streetAddress1,
              streetAddress2: pCode?.address?.streetAddress2,
              postalCode: pCode?.address?.zipCode,
              city: pCode?.address?.city,
            },
          },
          latitude: pCode?.latitude,
          longitude: pCode?.longitude,
          // units
        };
      } else {
        building = {
          ...building,
        };
      }

      /**
       * To assign unmarket state of unit which comes from snowflake
       * details table
       */

      building.unitInfo = _map(building.unitInfo, _unit => {
        const { isUnmarket } =
          _find(_availableUnits, unit => normalizeUnitId(unit.unitId) === normalizeUnitId(_unit.unitId)) || {};
        return { ..._unit, isUnmarket };
      });
      dispatch(buildingCodeSuccess(pCode));

      if (!_isEmpty(building)) {
        dispatch(buildingSuccess(building));
        return building;
      }
      return {};
    } catch (error) {
      if (!isLatestRequest()) return undefined;
      dispatch(buildingError(error));
      toastMessage("error", ERROR_MESSAGE.INFO);
      return throwError(error);
    }
  };
}

export function getMarketUsers(query) {
  return async dispatch => {
    dispatch(buildingRequest(LOADING.USERS_LOADING));
    try {
      const [err, response] = await to(EmployeeService.suggestions(query));
      if (err) throwError(err);
      dispatch(getMarketUsersSuccess());
      return response;
    } catch (error) {
      dispatch(buildingError(error));
      toastMessage("error", ERROR_MESSAGE.LIST);
      return throwError(error);
    }
  };
}

/**
 * To update audit status of a building
 * which only Admins and Auditors can perform
 */
export function updateBuildingAuditStatus(id, body) {
  return async dispatch => {
    try {
      const [err, response] = await to(buildingService.updateAuditStatus(id, body));
      await dispatch(getBuildingById(id));
      if (err) throwError(err);
    } catch (error) {
      dispatch(buildingError(error));
      toastMessage("error", ERROR_MESSAGE.EDITED);
      throwError(error);
    }
  };
}

/**
 * To update building record which doesn't have
 * never published status and
 * update audit status of a building
 * which only Admins and Auditors can perform
 */
export function buildingPublishAndPassAudit(id, buildingInfo, successMsg, auditStatus) {
  const { propertyCode } = buildingInfo.buildingInfo;
  return async (dispatch, getState) => {
    dispatch(buildingRequest(LOADING.BUILDING_LOADING));
    try {
      const clientState = getPropertySearchRedesignWriteState(getState());
      const [bynderErr, updatedBuildingInfo] = await to(
        buildingService.updatePropertyWithBynderDATUrls(id, buildingInfo),
      );
      if (bynderErr) throwError(bynderErr);

      const [err, response] = await to(
        buildingService.updateBuildingById(id, withPropertySearchRedesignState(updatedBuildingInfo, clientState)),
      );
      if (err) throwError(err);

      const [auditErr, auditResponse] = await to(buildingService.updateAuditStatus(propertyCode, auditStatus));
      if (err) throwError(err);

      toastMessage("success", successMsg || SUCCESS_MESSAGE.EDITED);
      dispatch(buildingRequest(LOADING.BUILDING_LOADING));
    } catch (error) {
      await refreshFeatureFlagsAfterWriteConflict(error, dispatch);
      dispatch(buildingError(error));
      toastMessage("error", ERROR_MESSAGE.EDITED);
      throwError(error);
    }
  };
}
